import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faCheck,
  faExclamationTriangle,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';

// Confirmation modal for every stage-mutating action: Advance / Rollback /
// Mark as Lost / Archive. The form passes the action type and stage
// labels; we render a focused confirmation prompt with an optional notes
// textarea.
//
// Props:
// - isOpen (bool): show / hide.
// - onClose(): user pressed cancel / X / Esc.
// - onConfirm(notes): user pressed confirm. Parent owns the network call.
// - action: 'advance' | 'rollback' | 'lost' | 'archive'.
// - fromStage / toStage: pretty-printed labels for the prompt body.
// - requireNotes (bool): when true, confirm is disabled until notes are
//   provided. Used for "Mark as Lost".
// - submitting (bool): show spinner + disable buttons while the parent
//   awaits the API response.
// - error (string | null): an optional inline error from the last API
//   attempt — surfaced above the action buttons.

const ACTION_COPY = {
  advance: {
    title: 'Advance enquiry stage',
    confirmLabel: 'Advance',
    bodyPrefix: 'Advance this enquiry from',
  },
  rollback: {
    title: 'Roll back enquiry stage',
    confirmLabel: 'Roll back',
    bodyPrefix: 'Roll this enquiry back from',
  },
  lost: {
    title: 'Mark enquiry as Lost',
    confirmLabel: 'Mark as Lost',
    bodyPrefix: 'Mark this enquiry as',
  },
  archive: {
    title: 'Archive enquiry',
    confirmLabel: 'Archive',
    bodyPrefix: 'Archive this enquiry',
    // Archive and Restore are not stage moves, so naming the current stage
    // here produced "Archive this enquiry Recapped?" -- a sentence that reads
    // like a bug. The stage chips belong to advance / rollback / lost.
    showStages: false,
    // The desk had been using Archive to mean "this one turned into a deal"
    // without being told what it costs: prod 2026-09-09 had 5 archived
    // enquiries and every single one was recapped. Say what happens.
    note: 'It comes off the enquiry list. You will still be able to open it '
      + 'from its recap or by switching the list to "Archived only", and '
      + 'anyone who can archive can restore it.',
  },
  unarchive: {
    title: 'Restore enquiry',
    confirmLabel: 'Restore',
    bodyPrefix: 'Put this enquiry back on the live list',
    showStages: false,
    note: 'It becomes editable again and reappears in the normal enquiry '
      + 'list. The record of who archived it, and when, is kept.',
  },
};

const StageAdvanceModal = ({
  isOpen,
  onClose,
  onConfirm,
  action = 'advance',
  fromStage,
  toStage,
  requireNotes = false,
  submitting = false,
  error = null,
}) => {
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) setNotes('');
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === 'Escape' && !submitting) {
        onClose?.();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, submitting, onClose]);

  if (!isOpen) return null;

  const copy = ACTION_COPY[action] || ACTION_COPY.advance;
  const trimmedNotes = notes.trim();
  const canSubmit = !submitting && (!requireNotes || trimmedNotes.length > 0);

  const handleConfirm = () => {
    if (!canSubmit) return;
    onConfirm?.(trimmedNotes);
  };

  return (
    <div
      className="fef-modal-overlay"
      onClick={() => !submitting && onClose?.()}
    >
      <div
        className="fef-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="fef-modal-header">
          <h3 className="fef-modal-title">{copy.title}</h3>
          <button
            type="button"
            className="fef-modal-close"
            onClick={() => !submitting && onClose?.()}
            disabled={submitting}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </header>

        <div className="fef-modal-body">
          <p className="fef-modal-prompt">
            {copy.bodyPrefix}
            {copy.showStages !== false && (
              <>
                {' '}
                {fromStage && (
                  <strong className="fef-modal-stage">{fromStage}</strong>
                )}
                {fromStage && toStage && ' to '}
                {toStage && (
                  <strong className="fef-modal-stage">{toStage}</strong>
                )}
              </>
            )}
            ?
          </p>

          {copy.note && (
            <p className="fef-modal-note">{copy.note}</p>
          )}

          <label className="fef-modal-notes-label" htmlFor="fef-modal-notes">
            Notes {requireNotes ? <span className="fef-modal-required">*</span> : <span className="fef-modal-optional">(optional)</span>}
          </label>
          <textarea
            id="fef-modal-notes"
            className="fef-modal-notes"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={submitting}
            placeholder={
              requireNotes
                ? 'Why is this enquiry being marked as Lost?'
                : 'Optional reason / context for the audit log'
            }
          />

          {error && (
            <div className="fef-modal-error">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error}</span>
            </div>
          )}
        </div>

        <footer className="fef-modal-footer">
          <button
            type="button"
            className="fef-modal-btn fef-modal-btn-secondary"
            onClick={() => !submitting && onClose?.()}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="fef-modal-btn fef-modal-btn-primary"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {submitting ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Working...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} /> {copy.confirmLabel}
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default StageAdvanceModal;
